import { useRef } from 'react';
import { Publicacion } from '../types';

interface Props {
  publicaciones: Publicacion[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onChange: (id: string, field: keyof Omit<Publicacion, 'id'>, value: string) => void;
  onFileChange: (id: string, nombre: string, file: File | null) => void;
  errors: Record<string, Record<string, string>>;
}

const BASES_DATOS = [
  {
    grupo: 'Bases Latinoamericanas',
    opciones: [
      'Latindex Directorio',
      'Latindex Catálogo 2.0',
      'SciELO',
      'Redalyc',
      'MIAR',
      'Sherpa Romeo',
      'DOAJ',
    ],
  },
  {
    grupo: 'Bases Internacionales',
    opciones: ['Scopus', 'Web of Science (WOS)'],
  },
  {
    grupo: 'Otras',
    opciones: ['Otras (especificar)'],
  },
];

export default function PublicacionesSection({
  publicaciones,
  onAdd,
  onRemove,
  onChange,
  onFileChange,
  errors,
}: Props) {
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Formatea bytes a KB / MB de forma legible
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelect = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Sin límite rígido — informamos si es muy pesado pero dejamos continuar
    if (file.size > 50 * 1024 * 1024) {
      // 50 MB es un límite razonable para el storage de Supabase gratuito
      const confirm = window.confirm(
        `El archivo pesa ${formatFileSize(file.size)}, lo que puede tardar bastante en subir. ¿Desea continuar?`
      );
      if (!confirm) {
        e.target.value = '';
        return;
      }
    }

    // Pasamos el File nativo directamente — sin conversión a base64
    onFileChange(id, file.name, file);
    onChange(id, 'link', '');
  };

  const handleRemoveFile = (id: string) => {
    onFileChange(id, '', null);
    if (fileRefs.current[id]) fileRefs.current[id]!.value = '';
  };

  return (
    <section id="publicaciones" className="max-w-5xl mx-auto px-6 mt-6">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#0D3157] rounded-lg">
              <i className="ri-file-text-line text-white text-sm" />
            </div>
            <div>
              <h2 className="text-[#0D3157] font-bold text-base">Publicaciones e Investigaciones</h2>
              <p className="text-slate-400 text-xs">Sección <strong className="text-slate-500">opcional</strong> — solo publicaciones o investigaciones <strong className="text-amber-600">finalizadas o publicadas</strong></p>
            </div>
          </div>
          <span className="bg-[#0D3157]/10 text-[#0D3157] text-xs font-bold px-2.5 py-1 rounded-full">
            {publicaciones.length} {publicaciones.length === 1 ? 'publicación' : 'publicaciones'}
          </span>
        </div>

        <div className="p-6 space-y-4">
          {/* Aviso importante */}
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500 mt-0.5 flex-shrink-0">
              <i className="ri-information-line text-base" />
            </div>
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>Esta sección es opcional.</strong> Si no tiene publicaciones, puede dejarla en blanco y enviar el formulario normalmente. Si tiene trabajos finalizados o publicados (artículos, libros, ponencias, tesis aprobadas, etc.), regístrelos aquí. No incluya trabajos en curso o borradores.
            </p>
          </div>

          {publicaciones.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-3">
                <i className="ri-file-add-line text-2xl text-slate-400" />
              </div>
              <p className="text-sm font-medium">Aún no ha agregado publicaciones</p>
              <p className="text-xs mt-1">Haga clic en &quot;Agregar publicación&quot; para comenzar</p>
            </div>
          )}

          {publicaciones.map((pub, index) => (
            <div
              key={pub.id}
              className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 relative"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#0D3157] rounded-full">
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-600">
                    Publicación {index + 1}
                  </span>
                </div>
                {publicaciones.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemove(pub.id)}
                    className="w-7 h-7 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    title="Eliminar publicación"
                  >
                    <i className="ri-delete-bin-line text-base" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3">
                {/* Título */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Título del trabajo / artículo
                    <span className="ml-1.5 text-slate-400 font-normal normal-case tracking-normal text-xs">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={pub.titulo}
                    onChange={(e) => onChange(pub.id, 'titulo', e.target.value)}
                    placeholder="Ingrese el título completo de la publicación"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] bg-white transition-colors ${errors[pub.id]?.titulo ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                  />
                  {errors[pub.id]?.titulo && (
                    <p className="text-red-500 text-xs mt-1">{errors[pub.id].titulo}</p>
                  )}
                </div>

                {/* Base de datos */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Base de datos indexada
                  </label>
                  <select
                    value={pub.baseDatos}
                    onChange={(e) => onChange(pub.id, 'baseDatos', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] bg-white cursor-pointer"
                  >
                    <option value="">— Seleccionar base de datos —</option>
                    {BASES_DATOS.map((grupo) => (
                      <optgroup key={grupo.grupo} label={grupo.grupo}>
                        {grupo.opciones.map((op) => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {pub.baseDatos === 'Otras (especificar)' && (
                    <input
                      type="text"
                      value={pub.baseDatosOtra}
                      onChange={(e) => onChange(pub.id, 'baseDatosOtra', e.target.value)}
                      placeholder="Especifique el nombre de la base de datos"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] bg-white mt-2"
                    />
                  )}
                </div>

                {/* URL o archivo */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Enlace de publicación o archivo adjunto
                  </label>

                  {pub.archivoNombre ? (
                    /* Archivo adjunto seleccionado */
                    <div className="flex items-center gap-2 border border-emerald-300 bg-emerald-50 rounded-lg px-3 py-2.5">
                      <div className="w-5 h-5 flex items-center justify-center text-emerald-600">
                        <i className="ri-file-check-line text-base" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-emerald-700 font-medium truncate">{pub.archivoNombre}</p>
                        {pub.archivoFile && (
                          <p className="text-xs text-emerald-500 mt-0.5">
                            {formatFileSize(pub.archivoFile.size)}
                            {pub.archivoFile.size > 5 * 1024 * 1024 && (
                              <span className="ml-2 text-amber-600 font-semibold">
                                · Archivo grande — la subida puede tardar unos segundos
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(pub.id)}
                        className="w-5 h-5 flex items-center justify-center text-emerald-500 hover:text-red-500 transition-colors cursor-pointer"
                        title="Quitar archivo"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                          <i className="ri-links-line text-slate-400 text-sm" />
                        </div>
                        <input
                          type="url"
                          value={pub.link}
                          onChange={(e) => onChange(pub.id, 'link', e.target.value)}
                          placeholder="https://doi.org/... o URL de la publicación"
                          className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] bg-white transition-colors"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => fileRefs.current[pub.id]?.click()}
                        className="flex items-center gap-1.5 border border-slate-300 hover:border-[#0D3157]/50 hover:bg-[#0D3157]/5 text-slate-600 hover:text-[#0D3157] font-semibold text-xs px-3 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        title="Subir archivo si no tiene URL"
                      >
                        <i className="ri-upload-2-line text-sm" />
                        Subir archivo
                      </button>
                    </div>
                  )}

                  <p className="text-xs text-slate-400 mt-1">
                    Si no cuenta con URL, puede subir el artículo en <strong>PDF</strong>, <strong>Word (.doc / .docx)</strong>. El archivo se sube al presionar <strong>Enviar Registro</strong>.
                  </p>

                  <input
                    ref={(el) => { fileRefs.current[pub.id] = el; }}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => handleFileSelect(pub.id, e)}
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={onAdd}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#0D3157]/30 text-[#0D3157] font-semibold text-sm py-3 rounded-lg hover:bg-[#0D3157]/5 hover:border-[#0D3157]/60 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-circle-line text-lg" />
            Agregar publicación / artículo
          </button>
        </div>
      </div>
    </section>
  );
}
